import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import NotFound from "./pages/NotFound";
import { useAuth } from './contexts/AuthContext';

// After a deploy, stale tabs hold an old index.html that references chunk hashes
// no longer on the server. Force one reload so the browser fetches the fresh
// index.html and the current hashes. sessionStorage flag prevents infinite loops
// if the chunk is genuinely missing.
const STALE_CHUNK_RELOAD_KEY = 'lazy-retry-reloaded';
const isStaleChunkError = (err) => {
  const msg = err?.message || '';
  return msg.includes('Failed to fetch dynamically imported module')
      || msg.includes('Importing a module script failed')
      || msg.includes('error loading dynamically imported module');
};
const lazyWithRetry = (factory) => lazy(() => factory().catch((err) => {
  if (isStaleChunkError(err) && !sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY)) {
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, '1');
    window.location.reload();
    return new Promise(() => {}); // suspend until reload
  }
  throw err;
}));

const DataManagement = lazyWithRetry(() => import('./pages/data-management'));
const Login = lazyWithRetry(() => import('./pages/login'));
const StyleCostingDashboard = lazyWithRetry(() => import('./pages/style-costing-dashboard'));
const StyleCostingForm = lazyWithRetry(() => import('./pages/style-costing-form'));
const CostComparisonTool = lazyWithRetry(() => import('./pages/cost-comparison-tool'));
const BarcodeScanner = lazyWithRetry(() => import('./pages/barcode-scanner'));
const BuyerMaster = lazyWithRetry(() => import('./pages/buyer-master'));
const EmailConfirmed = lazyWithRetry(() => import('./pages/email-confirmed'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, userRole } = useAuth();

  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && userRole !== 'admin') return <Navigate to="/style-costing-dashboard" replace />;

  return children;
};

const Routes = () => {
  const { user, loading } = useAuth();

  if (loading) return <PageLoader />;

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <RouterRoutes>
            <Route path="/" element={user ? <Navigate to="/style-costing-dashboard" replace /> : <Navigate to="/login" replace />} />
            <Route path="/login" element={user ? <Navigate to="/style-costing-dashboard" replace /> : <Login />} />
            <Route path="/email-confirmed" element={<EmailConfirmed />} />
            <Route path="/data-management" element={<ProtectedRoute adminOnly={true}><DataManagement /></ProtectedRoute>} />
            <Route path="/style-costing-dashboard" element={<ProtectedRoute><StyleCostingDashboard /></ProtectedRoute>} />
            <Route path="/style-costing-form" element={<ProtectedRoute><StyleCostingForm /></ProtectedRoute>} />
            <Route path="/cost-comparison-tool" element={<ProtectedRoute adminOnly={true}><CostComparisonTool /></ProtectedRoute>} />
            <Route path="/barcode-scanner" element={<ProtectedRoute><BarcodeScanner /></ProtectedRoute>} />
            <Route path="/buyer-master" element={<ProtectedRoute><BuyerMaster /></ProtectedRoute>} />
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
